import * as React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { useLocation } from "@reach/router";

const slugToLabel = (slug) =>
    slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const autoBreadcrumbs = (canonicalUrl, siteUrl) => {
    const path = canonicalUrl.replace(siteUrl, '').replace(/^\/|\/$/g, '');
    const segments = path.split('/').filter(Boolean);
    if (segments.length < 2) return null;
    return [
        { name: "Home", url: "/" },
        ...segments.map((seg, idx) => ({
            name: slugToLabel(seg),
            url: '/' + segments.slice(0, idx + 1).join('/') + '/',
        })),
    ];
};

function Seo({ description, title, canonical, image, article, imageAlt, prev, next, breadcrumbs }) {
	const { site } = useStaticQuery(
		graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
            image
          }
        }
      }
    `
	);

	const location = useLocation();

	const siteUrl = (site.siteMetadata.siteUrl || "").replace(/\/$/, "");
	const defaultTitle = site.siteMetadata.title;
	const metaDescription = description || site.siteMetadata.description;

	const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url);

	const normaliseUrl = (url) => {
		if (!url) return url;
		return url.endsWith("/") ? url : `${url}/`;
	};

	const joinSiteUrlWithPath = (path) => {
		if (!path) return `${siteUrl}/`;
		const safePath = path.startsWith("/") ? path : `/${path}`;
		return `${siteUrl}${safePath}`;
	};

	const currentPath = ((location?.pathname) || "").split(/[?#]/)[0];

	const canonicalUrl = normaliseUrl(
		canonical
			? isAbsoluteUrl(canonical)
				? canonical
				: joinSiteUrlWithPath(canonical)
			: joinSiteUrlWithPath(currentPath)
	);

	const defaultOgImageUrl = `${siteUrl}${site.siteMetadata.image}`;
	const ogImageUrl = image
		? (isAbsoluteUrl(image) ? image : `${siteUrl}${image}`)
		: defaultOgImageUrl;

	const explicitOgImageUrl = ogImageUrl || defaultOgImageUrl;

	// FinancialService entity with full sameAs
	const schemaOrgJSONLD = {
		"@context": "https://schema.org",
		"@type": ["FinancialService", "Organization"],
		url: siteUrl,
		name: defaultTitle,
		logo: `${siteUrl}/holborn-logo.png`,
		sameAs: [
			"https://twitter.com/holborn",
			"https://www.linkedin.com/company/holborn-assets-llc/",
			"https://www.facebook.com/HolbornAssets/",
			"https://www.instagram.com/holbornassets/",
			"https://www.youtube.com/c/HolbornAssetsLtd",
		],
	};

	// BreadcrumbList — use explicit prop if supplied, otherwise auto-generate from path
	const crumbs = Array.isArray(breadcrumbs)
		? breadcrumbs
		: autoBreadcrumbs(canonicalUrl, siteUrl);

	const breadcrumbSchema = crumbs?.length ? {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement": crumbs.map((crumb, idx) => ({
			"@type": "ListItem",
			position: idx + 1,
			name: crumb.name,
			item: isAbsoluteUrl(crumb.url) ? crumb.url : `${siteUrl}${crumb.url}`,
		})),
	} : null;

	// Pagination links
	const prevUrl = prev ? normaliseUrl(isAbsoluteUrl(prev) ? prev : `${siteUrl}${prev}`) : null;
	const nextUrl = next ? normaliseUrl(isAbsoluteUrl(next) ? next : `${siteUrl}${next}`) : null;

	return (
		<>
			<html lang="en-GB" />

			<title>{title ? `${title} | ${defaultTitle}` : defaultTitle}</title>

			<meta name="description" content={metaDescription} />
			<meta name="theme-color" content="#162054" />
			{explicitOgImageUrl ? <meta name="image" content={explicitOgImageUrl} /> : null}

			<link rel="canonical" href={canonicalUrl} />
			{prevUrl && <link rel="prev" href={prevUrl} />}
			{nextUrl && <link rel="next" href={nextUrl} />}

			<meta property="og:url" content={canonicalUrl} />
			<meta property="og:type" content={article ? "article" : "website"} />
			<meta property="og:title" content={title || defaultTitle} />
			<meta property="og:description" content={metaDescription} />
			{explicitOgImageUrl ? <meta property="og:image" content={explicitOgImageUrl} /> : null}
			<meta
				property="og:image:alt"
				content={imageAlt || title || defaultTitle}
			/>
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			<meta property="og:locale" content="en_GB" />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:creator" content={site.siteMetadata.author || ""} />
			<meta name="twitter:title" content={title || defaultTitle} />
			<meta name="twitter:description" content={metaDescription} />
			{explicitOgImageUrl ? <meta name="twitter:image" content={explicitOgImageUrl} /> : null}

			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(schemaOrgJSONLD),
				}}
			/>
			{breadcrumbSchema && (
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(breadcrumbSchema),
					}}
				/>
			)}
		</>
	);
}

export default Seo;
